/* eslint-disable react-hooks/exhaustive-deps */
import { Content } from '.generated/Feature.CW.model';
import { LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useRef, useState } from 'react';
import { PageSettings } from './BlogSearch';
import { SearchBox, SearchBoxHandle } from 'helpers/Coveo/SearchBox';
import { SearchProvider } from 'lib/utils/coveo/SearchProvider';
// import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import { useCoveoEngine } from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import { buildSearchBox } from '@coveo/headless';
import { useSearch } from 'lib/utils/coveo/SearchContext/SearchContext';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';

export type BlogSearchHeaderProps = Content.BlogSearchHeader & {
  searchFields?: string[];
  shouldRedirect?: boolean;
};

const BlogSearchHeader = ({
  shouldRedirect = true,
  ...props
}: BlogSearchHeaderProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchBorderColor = isSearchFocused ? 'border-blue-base' : 'border-gray-100';
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;
  const blogSearchPage = contextWithSettings?.blogSearchPage as LinkField;
  const searchBoxRef = useRef<SearchBoxHandle>(null);
  const targetHref = blogSearchPage?.value?.href || '/';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const router = useRouter();
  const engine = useCoveoEngine();
  const { setContextResults } = useSearch();
  const isEditing = useIsEditing();

  useEffect(() => {
    const searchTerm = router.query.q as string;

    if (searchTerm && searchBoxRef.current) {
      searchBoxRef.current.setCurrentValue(searchTerm);
    }
  }, [router.query.q]);

  const escapeForCoveo = (str: string = ''): string => {
    return str.replace(/([+\-&|!(){}[\]^"~*?:\\/])/g, '\\$1');
  };
  const handleSearchClick = async () => {
    const searchTerm = searchBoxRef.current?.getCurrentValue()?.trim();

    if (shouldRedirect && targetHref && currentPath !== targetHref) {
      const query: Record<string, string> = {};
      if (searchTerm) query.q = searchTerm;

      await router.push({
        pathname: targetHref,
        query,
      });

      // Do not proceed further if redirected; destination page handles search
      return;
    }

    if (!engine) return;

    const aqParts: string[] = [];

    if (searchTerm && props?.searchFields?.length) {
      const escapedSearchTerm = escapeForCoveo(searchTerm);
      const searchConditions = props?.searchFields?.map(
        (field) => `@${field}==*${escapedSearchTerm}*`
      );
      aqParts.push(`(${searchConditions.join(' OR ')})`);
    }

    const controller = buildSearchBox(engine, { options: { numberOfSuggestions: 5 } });
    controller.updateText(searchTerm || '');
    controller.submit();

    engine.subscribe(() => {
      const fullSearchState = engine.state.search;
      setContextResults(fullSearchState);
    });
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearchClick();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  return (
    <section className="bg-blue-50 relative">
      {/* Left content */}
      <div className="z-[9] relative flex w-full max-w-[1440px] mx-auto">
        <div className="w-full lg:w-1/2 lg:py-[65px] py-[48px] lg:pl-[80px] lg:pr-[40px] px-10 lg:px-0">
          <h1 className={`${props?.fields?.hidePageTitle?.value && 'hidden'}`}>
            {props?.fields?.pageTitle?.value}
          </h1>
          <div>
            <NextImage
              src="/assets/Images/AtEveryTurn.png"
              alt={'At every turn page'}
              width={325}
              height={400}
              className="mb-[40px]"
            />
          </div>

          {/* Search Bar */}
          <SearchProvider
            searchHub={contextWithSettings?.blogSRSearchHub?.value || ''}
            pipeline={contextWithSettings?.blogSRPipeline?.value || ''}
            accessToken={contextWithSettings?.blogSRAccessToken?.value || ''}
            organizationId={process.env.NEXT_PUBLIC_COVEO_ORG_ID || ''}
            analyticsEnabled={false}
          >
            <div className="lg:flex gap-[10px]">
              <div
                role="search"
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className={`w-full border-2  ${searchBorderColor} rounded-sm transition-colors duration-200`}
              >
                <SearchBox
                  ref={searchBoxRef}
                  placeholder={props?.fields?.searchTermPlaceholderText}
                  onSubmitRedirect={false}
                  onClear={() => setIsSearchFocused(false)}
                  onSubmit={() => handleSearchClick()}
                />
              </div>

              <ButtonWrapper
                ctaVariant="primary"
                className="block px-12 py-4 font-semibold !text-white !bg-blue-600 hover:!bg-blue-700 mt-5 lg:mt-0 w-full lg:w-fit focus:!outline-offset-2 focus:!outline-2 focus:!outline-blue-600 focus:!border-blue-600 focus:!outline focus:!ring-0"
                ctaIcon="search"
                ctaIconAlignment="left"
                onClick={handleSearchClick}
                disabled={isEditing}
              >
                {getDictionaryValue('SearchButtonLabel')}
              </ButtonWrapper>
            </div>
          </SearchProvider>
        </div>
      </div>
      <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
        <NextImage
          src="/assets/Images/blogSearchHero.png"
          alt={'blog search header'}
          fill
          className="object-cover"
        />
      </div>
    </section>
  );
};

export default BlogSearchHeader;
